import { Component } from '@theme/component';

/**
 * <tabs-component> — switches the product tab panels of the lc-tabs section.
 * The initial active tab is rendered server-side; aria-selected is the single source of truth.
 * The sliding line is the tablist's ::after, positioned via CSS variables; tabs and panels pair by index.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} tablist
 * @property {HTMLAnchorElement[]} tabs
 * @property {HTMLElement[]} panels
 *
 * @extends {Component<Refs>}
 */
class TabsComponent extends Component {
  requiredRefs = ['tablist', 'tabs', 'panels'];

  // Also fires once on observe, which handles the initial placement.
  #resizeObserver = new ResizeObserver(() => this.#updateActiveLine());

  /** @type {IntersectionObserver | null} */
  #preloadObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('shopify:block:select', this.#handleBlockSelect);
    // The line is relative to the tablist, so only its size matters (fonts, breakpoints, resize).
    this.#resizeObserver.observe(this.refs.tablist);
    this.#observePreload();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    this.#resizeObserver.disconnect();
    this.#preloadObserver?.disconnect();
  }

  /**
   * Activates the clicked tab.
   * @param {MouseEvent} event
   */
  selectTab(event) {
    event.preventDefault();
    this.#activate(this.refs.tabs.indexOf(/** @type {HTMLAnchorElement} */ (event.target)));
  }

  /**
   * Moves focus and selection with the arrow keys (WAI-ARIA tabs pattern).
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    const offset = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!offset) return;

    event.preventDefault();
    const { tabs } = this.refs;
    const index = (tabs.indexOf(/** @type {HTMLAnchorElement} */ (event.target)) + offset + tabs.length) % tabs.length;
    this.#activate(index);
    tabs[index]?.focus();
  }

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    this.#activate(this.refs.panels.indexOf(/** @type {HTMLElement} */ (event.target)));
  };

  /** @param {number} index */
  #activate(index) {
    const { tabs, panels } = this.refs;
    if (!tabs[index]) return;

    tabs.forEach((tab, tabIndex) => {
      const isActive = tabIndex === index;
      const panel = panels[tabIndex];

      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
      if (panel) panel.hidden = !isActive;
    });

    this.#updateActiveLine(true);
  }

  /** Lazy images in hidden panels only load on tab switch, which shows as a delay; fetch them as the section nears the viewport. */
  #observePreload() {
    // From 990px Horizon scrolls .page-wrapper, which clips a viewport-based rootMargin.
    const scroller = this.closest('.page-wrapper');
    const root = scroller && getComputedStyle(scroller).overflowY === 'auto' ? scroller : null;

    this.#preloadObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.#preloadHiddenPanelImages();
    }, { root, rootMargin: '200px' });
    this.#preloadObserver.observe(this);
  }

  /** Without the lazy attribute the browser fetches the images even inside hidden panels. */
  #preloadHiddenPanelImages() {
    this.#preloadObserver?.disconnect();
    for (const image of this.querySelectorAll('.tab-panel[hidden] img[loading="lazy"]')) {
      // sizes="auto" measures the hidden (0px) image and would pick the largest source.
      image.setAttribute('sizes', (image.getAttribute('sizes') ?? '').replace(/^auto,\s*/, ''));
      image.removeAttribute('loading');
    }
  }

  /**
   * Aligns the line with the active tab; only user-triggered tab changes animate.
   * @param {boolean} [animate]
   */
  #updateActiveLine(animate = false) {
    const { tablist, tabs } = this.refs;
    const activeTab = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    if (!activeTab) return;

    // Sub-pixel values; offsetLeft/offsetWidth round to whole pixels.
    const tabRect = activeTab.getBoundingClientRect();
    const left = `${tabRect.left - tablist.getBoundingClientRect().left}px`;
    const width = `${tabRect.width}px`;
    // Skip no-op updates so a redundant resize callback can't cancel a running transition.
    if (tablist.style.getPropertyValue('--active-line-left') === left && tablist.style.getPropertyValue('--active-line-width') === width) return;

    // Layout corrections (late CSS/font loads, resize) must jump, not slide in.
    tablist.toggleAttribute('data-line-static', !animate);
    tablist.style.setProperty('--active-line-left', left);
    tablist.style.setProperty('--active-line-width', width);
  }
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}
